import React, { useState } from "react";
import { Table, Button, Modal, Form } from "react-bootstrap";
import { FaCheck, FaTimes, FaEdit } from "react-icons/fa";
import { parseISO, format } from "date-fns";
import { toZonedTime, fromZonedTime } from "date-fns-tz";

import axios from "axios";
import { API_BASE_URL } from "../../api";

const LeaderProjectView = ({ projectLogs, onUpdateLog, isProjectLeader }) => {
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingLog, setEditingLog] = useState(null);
  const [editedStartTime, setEditedStartTime] = useState("");
  const [editedEndTime, setEditedEndTime] = useState("");
  const [logUpdateError, setLogUpdateError] = useState(null);
  const [logUpdateSuccess, setLogUpdateSuccess] = useState(false);

  const handleApprove = (logId) => {
    onUpdateLog(logId, { status: "approved" });
  };

  const handleReject = (logId) => {
    onUpdateLog(logId, { status: "rejected" });
  };

  const handleEditClick = (log) => {
    setEditingLog(log);
    setEditedStartTime(formatDateTimeForInput(log.startTime));
    setEditedEndTime(formatDateTimeForInput(log.endTime));
    setLogUpdateError(null);
    setLogUpdateSuccess(false);
    setShowEditModal(true);
  };

  const formatDate = (date) => {
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(new Date(date));
  };

  // const formatDateTimeForInput = (dateString) => {
  //   if (!dateString) return "";
  //   const date = new Date(dateString);
  //   return date.toISOString().slice(0, 16);
  // };

  const getAuthHeader = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const handleEditSubmit = async (e) => {
    if (e) e.preventDefault();
    setLogUpdateError(null);
    setLogUpdateSuccess(false);

    if (!editedStartTime || !editedEndTime) {
      setLogUpdateError("Please select both start and end times.");
      return;
    }

    try {
      const start = new Date(editedStartTime);
      const end = new Date(editedEndTime);

      if (end <= start) {
        setLogUpdateError("End time must be after start time.");
        return;
      }

      const diffMs = end - start;
      const diffHours = diffMs / (1000 * 60 * 60);

      const response = await axios.put(
        `${API_BASE_URL}/participations/update-log/${editingLog._id}`,
        {
          startTime: start.toISOString(),
          endTime: end.toISOString(),
          hours: diffHours,
        },
        { headers: getAuthHeader() }
      );

      onUpdateLog(editingLog._id, response.data);
      setLogUpdateSuccess(true);
      setEditedStartTime("");
      setEditedEndTime("");

      setTimeout(() => {
        setShowEditModal(false);
        setLogUpdateSuccess(false);
      }, 1500);
    } catch (error) {
      setLogUpdateError(
        error.response?.data?.message || error.response?.data?.error || "Failed to update log. Please try again."
      );
    }
  };
  // const formatDateTime = (dateString) => {
  //   if (!dateString) return "N/A";
  //   const date = toZonedTime(parseISO(dateString), "Africa/Johannesburg");
  //   return format(date, "yyyy-MM-dd HH:mm:ss");
  // };

  const formatDateTime = (date) => {
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(new Date(date));
  };

  const formatDateTimeForInput = (dateString) => {
    if (!dateString) return "";
    const date = toZonedTime(parseISO(dateString), "Africa/Johannesburg");
    return format(date, "yyyy-MM-dd'T'HH:mm");
  };

  return (
    <>
      <Table striped bordered hover>
        <thead>
          <tr>
            <th>Participant</th>
            <th>Start</th>
            <th>End</th>
            <th>Hours Logged</th>
            <th>Reflection & Evidence</th>
            <th>Status</th>
            {isProjectLeader && <th>Actions</th>}
          </tr>
        </thead>
        <tbody>
          {projectLogs.map((log) => (
            <tr key={log._id}>
              <td>{log.participantName || "Unknown"}</td>
              <td>{formatDateTime(log.startTime)}</td>
              <td>{formatDateTime(log.endTime)}</td>
              <td>{log.hours}</td>
              <td style={{ minWidth: "180px" }}>
                {log.reflection ? <div className="small mb-1">{log.reflection}</div> : <span className="text-muted small">No reflection</span>}
                {log.evidence?.length > 0 && (
                  <div className="d-flex flex-wrap gap-1 mt-1">
                    {log.evidence.map((url, index) => (
                      <a key={url} href={url} target="_blank" rel="noreferrer" className="btn btn-sm btn-outline-secondary">
                        Evidence {index + 1}
                      </a>
                    ))}
                  </div>
                )}
              </td>
              <td>{log.status}</td>
              {isProjectLeader && (
                <td>
                  <Button
                    variant="success"
                    size="sm"
                    onClick={() => handleApprove(log._id)}
                    disabled={log.status === "approved"}>
                    <FaCheck /> Approve
                  </Button>{" "}
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => handleReject(log._id)}
                    disabled={log.status === "rejected"}>
                    <FaTimes /> Reject
                  </Button>{" "}
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => handleEditClick(log)}>
                    <FaEdit /> Edit Times
                  </Button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </Table>
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Log Times</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {logUpdateError && <div className="alert alert-danger mb-3">{logUpdateError}</div>}
          {logUpdateSuccess && <div className="alert alert-success mb-3">Log updated successfully!</div>}
          <Form onSubmit={handleEditSubmit}>
            <Form.Group controlId="editStartTime">
              <Form.Label>Start Time</Form.Label>
              <Form.Control
                type="datetime-local"
                value={editedStartTime}
                onChange={(e) => setEditedStartTime(e.target.value)}
              />
            </Form.Group>
            <Form.Group controlId="editEndTime">
              <Form.Label>End Time</Form.Label>
              <Form.Control
                type="datetime-local"
                value={editedEndTime}
                onChange={(e) => setEditedEndTime(e.target.value)}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Close
          </Button>
          <Button variant="primary" onClick={handleEditSubmit}>
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default LeaderProjectView;
